import { Injectable } from '@angular/core';
import {ObjectService} from "./abstractions/objects/object.service.abstract";
import {ChatbotRegressionTestCase, IChatbotOptions, ISampleMessage} from "../../models/chatbot-options.model";
import {map, Observable, timeout} from "rxjs";
import {environment} from '../../../environments/environment';
import {FigmaProductCardDraft, FigmaProductCardEditor, FigmaProductCardMaster, RefreshFigmaDesignResponse} from '../../models/figma-product-card.model';
import {IConversation} from "../../models/conversation.model";
import {
  ChatbotVersionOptions,
  PublishChatbotDraftResponse,
  UpdateChatbotDraftRequest
} from "../../models/chatbot-version.model";

@Injectable({
  providedIn: 'root'
})
export class ChatbotsService extends ObjectService<IChatbotOptions> {

  protected api = '/shop-assistants/'; // Base URL for the client API

  getFigmaProductCardEditor(clientId: string, chatbotId: string): Observable<FigmaProductCardEditor> {
    return this.http.get<FigmaProductCardEditor>(this.figmaEndpoint(clientId, chatbotId)).pipe(timeout(15000));
  }

  saveFigmaProductCardDraft(clientId: string, chatbotId: string, draft: FigmaProductCardDraft): Observable<FigmaProductCardDraft> {
    return this.http.put<FigmaProductCardDraft>(this.figmaEndpoint(clientId, chatbotId), draft).pipe(timeout(15000));
  }

  saveFigmaProductCardMaster(clientId: string, chatbotId: string,
    master: Omit<FigmaProductCardMaster, 'standardSettings'>): Observable<FigmaProductCardMaster> {
    return this.http.put<FigmaProductCardMaster>(`${this.figmaEndpoint(clientId, chatbotId)}/master`, master).pipe(timeout(15000));
  }

  private figmaEndpoint(clientId: string, chatbotId: string): string {
    return `${environment.aiBaseServerUrl}${encodeURIComponent(clientId)}/shop-assistants/${encodeURIComponent(chatbotId)}/figma-product-card`;
  }

  refreshFigmaDesign(clientId: string, chatbotId: string, request: {designUrl: string; revision: string}): Observable<RefreshFigmaDesignResponse> {
    return this.http.post<RefreshFigmaDesignResponse>(`${this.figmaEndpoint(clientId, chatbotId)}/refresh`, request).pipe(timeout(100000));
  }

  proposeInitialInstructionForChatbotAsync(chatbotId: string): Observable<Record<string, string>> {
    return this.http.post<Record<string, string>>(this.getEndpoint() + chatbotId + '/propose-initial-instruction', {});
  }

  summarizeLastWeekAsync(chatbotId: string): Observable<string> {
    return this.http.get<string>(this.getEndpoint() + chatbotId + '/last-week-summary');
  }

  proposeSampleMessagesAsync(chatbotId: string): Observable<ISampleMessage[]> {
    return this.http.get<ISampleMessage[]>(this.getEndpoint() + chatbotId + '/propose-sample-messages');
  }

  uploadQuickStartImageAsync(
    chatbotId: string,
    file: File
  ): Observable<{ url: string; assetId: string; contentType: string; size: number }> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<{
      assetId: string;
      path: string;
      contentType: string;
      size: number;
    }>(`${this.getEndpoint()}${encodeURIComponent(chatbotId)}/quick-start-assets`, formData).pipe(
      map(response => ({
        assetId: response.assetId,
        contentType: response.contentType,
        size: response.size,
        url: new URL(response.path, environment.aiBaseServerUrl).toString()
      }))
    );
  }

  localizeAsync(chatbotId: string, force: boolean = false): Observable<{ sourceHash: string; generatedLocales: string[]; skippedLocales: string[] }> {
    return this.http.post<{ sourceHash: string; generatedLocales: string[]; skippedLocales: string[] }>(
      this.getEndpoint() + chatbotId + '/localize',
      { force }
    );
  }

  approveMasterThreadAsync(chatbotId: string): Observable<void> {
    return this.http.post<void>(this.getEndpoint() + chatbotId + '/approve-thread', {});
  }

  getMasterThreadsForChatbotAsync(chatbotId: string): Observable<IConversation[]> {
    return this.http.get<IConversation[]>(this.getEndpoint() + chatbotId + '/master-threads');
  }

  reportAsync(id: string, startDate?: string, endDate?: string): Observable<any> {
    return this.http.get<any>(this.getEndpoint() + id + '/report?startDate=' + startDate + '&endDate=' + endDate);
  }

  reportAllAsync(startDate?: string, endDate?: string): Observable<any> {
    return this.http.get<any>(this.getEndpoint() + 'report?startDate=' + startDate + '&endDate=' + endDate);
  }

  getHubSpotInboxesAsync(chatbotId: string): Observable<{ id: string; name: string }[]> {
    return this.http.get<{ id: string; name: string }[]>(
      this.getEndpoint() + chatbotId + '/hubspot/inboxes'
    );
  }

  getVersionsAsync(chatbotId: string): Observable<ChatbotVersionOptions[]> {
    return this.http.get<ChatbotVersionOptions[]>(`${this.getEndpoint()}${chatbotId}/versions`);
  }

  getRegressionTestsAsync(chatbotId: string): Observable<ChatbotRegressionTestCase[]> {
    return this.http.get<ChatbotRegressionTestCase[]>(`${this.getEndpoint()}${chatbotId}/versions/regression-tests`);
  }

  saveRegressionTestsAsync(
    chatbotId: string,
    testCases: ChatbotRegressionTestCase[]
  ): Observable<ChatbotRegressionTestCase[]> {
    return this.http.post<ChatbotRegressionTestCase[]>(
      `${this.getEndpoint()}${chatbotId}/versions/regression-tests`,
      {testCases}
    );
  }

  createDraftAsync(chatbotId: string, sourceVersionId?: string): Observable<ChatbotVersionOptions> {
    return this.http.post<ChatbotVersionOptions>(`${this.getEndpoint()}${chatbotId}/versions/drafts`, {
      sourceVersionId
    });
  }

  updateDraftAsync(
    chatbotId: string,
    draftId: string,
    request: UpdateChatbotDraftRequest
  ): Observable<ChatbotVersionOptions> {
    return this.http.post<ChatbotVersionOptions>(
      `${this.getEndpoint()}${chatbotId}/versions/drafts/${draftId}/update`,
      request
    );
  }

  publishDraftAsync(chatbotId: string, draftId: string): Observable<PublishChatbotDraftResponse> {
    return this.http.post<PublishChatbotDraftResponse>(
      `${this.getEndpoint()}${chatbotId}/versions/drafts/${draftId}/publish`,
      {}
    );
  }

  rollbackAsync(chatbotId: string, versionId: string): Observable<PublishChatbotDraftResponse> {
    return this.http.post<PublishChatbotDraftResponse>(
      `${this.getEndpoint()}${chatbotId}/versions/${versionId}/rollback`,
      {}
    );
  }

}
